/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2021 - 2026 (original work) Open Assessment Technologies SA
 */

/* eslint-disable no-console */

import { recoverInteractionsState } from '@/store/interactions';
import { get } from 'svelte/store';
import recoverableErrorsManager from '@/core/recoverableErrorsManager/recoverableErrorsManager';
import { io } from 'socket.io-client';
import { decodedAccessToken as decodedAccessTokenStore } from '@/store/accessToken';
import { ltiClaims } from '@/core/utils/consts';

let socket = null;

export function getSocketConnectionParams(webSocketUrl) {
    try {
        const parsedWebSocketUrl = new URL(webSocketUrl);
        const socketServerUrl = parsedWebSocketUrl.origin;
        const normalizedPathname = parsedWebSocketUrl.pathname.replace(/\/+$/, '');
        const socketPath = normalizedPathname ? `${normalizedPathname}/socket.io/` : '/socket.io/';

        return { socketServerUrl, socketPath };
    } catch {
        // do nothing if url is simple
        return {
            socketServerUrl: webSocketUrl,
            socketPath: null
        };
    }
}

/**
 * Inits webSocket connection and sets logic to manage incoming messages. If
 * WS connection was already set, it is closed before starting a new one
 * @param {string} webSocketUrl - webSocket url
 * @param {string} contextId - context Id
 */
export function initWebSocket(webSocketUrl, contextId) {
    if (!webSocketUrl) {
        console.error('invalid socket url', webSocketUrl);
        return;
    }

    console.log('on initWebSocket', webSocketUrl);
    closeWebSocket();

    const jwtToken = get(decodedAccessTokenStore);
    const context = contextId
        ? contextId
        : (jwtToken && jwtToken[ltiClaims.context] && jwtToken[ltiClaims.context]['id']) || 'default_local';
    const isFilterByUserId = jwtToken?.[ltiClaims.custom]?.['filter.byUserId'] === 'true';
    const accessToken = sessionStorage.getItem('accessToken');

    console.log('setting access token to the socket: ', accessToken);

    const socketClientOptions = {
        reconnectionAttempts: 8,
        transports: ['websocket', 'polling'], // dev only! - would prefer default: ['polling', 'websocket']
        auth: {
            token: accessToken
        },
        query: {
            contextId: context
        }
    };

    const { socketServerUrl, socketPath } = getSocketConnectionParams(webSocketUrl);
    if (socketPath) {
        socketClientOptions.path = socketPath;
    }

    if (isFilterByUserId) {
        socketClientOptions.query.filterByUserId = 'true';
    }
    socket = io(socketServerUrl, socketClientOptions);
    socket.on('status-updates', events => {
        const interactions = Object.values(events || {})
            .map(event => event?.data)
            .filter(Boolean);
        recoverInteractionsState(interactions);
    });

    socket.on('connect_error', () => {
        // eslint-disable-next-line no-console
        console.error('connect_error');
    });
    socket.on('disconnect', () => {
        // eslint-disable-next-line no-console
        console.log('disconnected', socket.id);
    });
    socket.on('unexpected-error', d => {
        // eslint-disable-next-line no-console
        console.error('unexpected-error', d);
    });

    socket.onclose = function (closeEvent) {
        if (closeEvent.code === 1001) {
            recoverableErrorsManager.setOffline();
        }
    };
}

/**
 * Closes websocket connection, in case it is open
 */
export function closeWebSocket() {
    if (socket) {
        socket.disconnect();
    }
}
